/**
 * persistence/middleware/persistenceMiddleware.js
 *
 * Redux middleware that intercepts SET_FULL_ANALYSE_STRING and drives the
 * entire persistence pipeline.
 *
 * Processing order on every SET_FULL_ANALYSE_STRING action:
 *   1. Tab-lock guard   — skip all writes in non-primary tabs
 *   2. Diff computation — compare prev vs next string; skip if no change
 *   3. IndexedDB write  — debounced session upsert (200ms)
 *   4. Diff log         — fire-and-forget diffLog append
 *   5. Checkpoint eval  — time + event-count trigger evaluation
 *   6. Sync queue       — schedule REST API push (typing-pause debounce)
 *
 * Why middleware over store.subscribe()?
 *   - Receives the raw action payload (matchId, string) without re-reading state
 *   - Fires only for the exact action type we care about
 *   - Does NOT add per-component logic to InputBoard
 *   - Zero reducer changes required
 *
 * Tab-lock guard (critical):
 *   redux-state-sync broadcasts Redux actions to ALL open tabs via
 *   BroadcastChannel.  Without this guard, a secondary tab would receive
 *   the replayed SET_FULL_ANALYSE_STRING and write duplicate IndexedDB records.
 *   The guard checks localStorage against sessionStorage to identify the
 *   primary tab.
 *
 * Important: this middleware is side-effect-only, with one exception — the
 *   cross-match guard (stringGuard.guardStringUpdate).  A locally dispatched
 *   update that would replace most of the current string (or a string copied
 *   from another match) is confirmed first; if refused, the action is passed on
 *   with the current string so the UI snaps back and nothing is persisted.
 */

import { SET_FULL_ANALYSE_STRING, FETCH_MATCH_STRING_SERVER_SUCCESS } from "../../redux/actions/types";
import { upsertSession }           from "../managers/SessionManager";
import { computeDiff, appendDiffLog } from "../managers/DiffManager";
import { evaluate as evaluateCheckpoint } from "../managers/CheckpointManager";
import { scheduleApiSync }         from "../managers/SyncManager";
import { apply as applyToBranch }  from "../vcs/StringBridge";
import { TAB_LOCK_KEY }            from "../config";
import { guardStringUpdate, noteChangeSource, markLocalEdit } from "../guard/stringGuard";

// ─── State tracking ───────────────────────────────────────────────────────────

/**
 * Previous string per session, held in memory to compute diffs.
 * Key: `${matchId}__${analystId}` → string
 */
const previousStrings = new Map();

/**
 * Last string fetched from the server per match. The InputBoards echo a fetch
 * back as SET_FULL_ANALYSE_STRING, which must not count as a local edit.
 * Key: matchId → string
 */
const serverStrings = new Map();

// ─── Tab guard ────────────────────────────────────────────────────────────────

/**
 * Returns true if the current tab holds the tab-lock (is the primary tab).
 * Reads from localStorage (written by App.js useTabLock) and compares
 * against this tab's sessionStorage id.
 *
 * Falls back to true when sessionStorage/localStorage is unavailable so
 * IndexedDB writes always happen in environments that don't support tab-locking
 * (e.g., test environments, some mobile browsers).
 */
function isPrimaryTab() {
  try {
    const lockRaw  = localStorage.getItem(TAB_LOCK_KEY);
    const myTabId  = sessionStorage.getItem("tab_id");

    if (!lockRaw || !myTabId) return true; // can't determine — assume primary

    const lock = JSON.parse(lockRaw);
    return lock?.tabId === myTabId;
  } catch {
    return true; // safe default
  }
}

// ─── Middleware ───────────────────────────────────────────────────────────────

/**
 * The persistence middleware factory.
 * Register this in store.js alongside ReduxThunk and createStateSyncMiddleware.
 */
const persistenceMiddleware = (store) => (next) => (action) => {
  if (action.type === FETCH_MATCH_STRING_SERVER_SUCCESS && action.payload?.id) {
    serverStrings.set(String(action.payload.id), action.payload.string);
  }
  if (action.type !== SET_FULL_ANALYSE_STRING) return next(action);

  // ── Cross-match guard ────────────────────────────────────────────────────
  // Only for actions dispatched in this tab — $isSync marks redux-state-sync
  // replays from other tabs, which were already guarded where they started.
  let changeSource = action.payload?.source ?? null;
  if (!action.$isSync && action.payload?.id && typeof action.payload?.string === "string") {
    const guardId    = action.payload.id;
    const prevInRedux = store.getState()?.control?.analysedString?.[guardId] ?? "";
    const verdict    = guardStringUpdate(guardId, prevInRedux, action.payload.string);
    if (!verdict.allow) {
      return next({ ...action, payload: { ...action.payload, string: prevInRedux } });
    }
    changeSource = verdict.source ?? changeSource;
  }

  // ── Pass action through to reducers first ────────────────────────────────
  // The reducer always runs. We act on the side only.
  const result = next(action);

  const { id: matchId, string: nextString } = action.payload ?? {};

  // Guard: malformed action
  if (!matchId || nextString === undefined) return result;

  // ── Tab-lock guard ───────────────────────────────────────────────────────
  // Skip all persistence writes in non-primary tabs.
  // The primary tab owns IndexedDB writes for this session.
  if (!isPrimaryTab()) return result;

  const state     = store.getState();
  const analystId = String(state?.auth?.analyst_id ?? "");
  const token     = state?.auth?.token ?? "";

  if (!analystId) return result; // auth not loaded yet

  // ── Diff computation ─────────────────────────────────────────────────────
  const sessionKey  = `${matchId}__${analystId}`;
  const prevString  = previousStrings.get(sessionKey) ?? "";
  const diff        = computeDiff(prevString, nextString);

  // Skip if string is completely unchanged (e.g. Redux re-hydration)
  if (!diff.hasChange && prevString !== "") return result;

  // The first string seen on this page is what was loaded, not an edit.
  const isLocalEdit =
    diff.hasChange &&
    previousStrings.has(sessionKey) &&
    nextString !== serverStrings.get(String(matchId));

  // Update previous string cache
  previousStrings.set(sessionKey, nextString);

  // Recorded on the next commit so Firestore history shows how it changed.
  if (diff.hasChange) noteChangeSource(matchId, analystId, changeSource ?? "edit");
  if (isLocalEdit) markLocalEdit(matchId, analystId);

  // ── 1. IndexedDB session write (debounced 200ms) ─────────────────────────
  upsertSession({ matchId, analystId, currentString: nextString });

  // ── 2. Diff log append (fire-and-forget) ─────────────────────────────────
  if (diff.hasChange) {
    appendDiffLog({ matchId, analystId, diff, baseVersionId: null });
  }

  // ── 3. Checkpoint evaluation ─────────────────────────────────────────────
  // Synchronous evaluation, async write — never blocks the Redux action
  evaluateCheckpoint(matchId, analystId, nextString);

  // ── 3b. VCS branch sync ───────────────────────────────────────────────────
  // Reconcile the live string into the analyst's working branch (Yjs doc).
  // Fire-and-forget + internally debounced — never blocks the Redux action.
  if (diff.hasChange) {
    applyToBranch({ matchId, analystId, nextString });
  }

  // ── 4. Schedule REST API sync ─────────────────────────────────────────────
  // Resets the typing-pause debounce timer on every action.
  // The actual API call fires after API_SYNC_TYPING_PAUSE_MS of inactivity.
  if (token) {
    scheduleApiSync({ matchId, analystId, string: nextString, token });
  }

  return result;
};

export default persistenceMiddleware;
